import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Btn, AInput, useSave } from "../AdminUI";
import { Send, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";

const Toggle = ({ label, checked, onChange, hint, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-0">
    <div><p className="font-semibold text-sm text-[var(--navy)]">{label}</p>{hint && <p className="text-xs text-gray-400">{hint}</p>}</div>
    <button onClick={() => onChange(!checked)} data-testid={testid}
      className={`shrink-0 w-11 h-6 rounded-full transition-colors ${checked ? "bg-green-500" : "bg-gray-300"} relative`}>
      <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </div>
);

const Row = ({ label, value, ok }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="font-semibold text-[var(--navy)]">{value ?? (ok ? "Connected" : "—")}</span>
  </div>
);

const VerifiedRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="font-semibold flex items-center gap-1.5 text-green-600">
      <CheckCircle2 className="w-4 h-4" /> {value || "Verified"}
    </span>
  </div>
);

export default function EmailSettings() {
  const [s, setS] = useState(null);
  const [saving, save] = useSave();
  const [testing, setTesting] = useState(false);
  const load = () => api.get("/admin/email-settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;

  const set = (k, v) => setS({ ...s, [k]: v });
  const submit = () => save(() => api.patch("/admin/email-settings", {
    email_enabled: s.email_enabled,
    email_sender_name: s.email_sender_name, email_reply_to: s.email_reply_to,
    email_notify_to: s.email_notify_to,
  }));
  const sendTest = async () => {
    setTesting(true);
    try { const r = await api.post("/admin/email/test"); toast.success(`Test email: ${r.data.result}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); load(); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Email Settings</h1>
        <div className="flex gap-2">
          <Btn variant="blue" onClick={sendTest} loading={testing} data-testid="send-test-email"><Send className="w-4 h-4" /> Send Test Email</Btn>
          <Btn onClick={submit} loading={saving} data-testid="email-settings-save">Save</Btn>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Connection">
          <Row label="Provider" value={s.provider} />
          <Row label="SMTP Host" value={s.host} />
          <Row label="Port" value={`${s.port} (${s.encryption})`} />
          <Row label="Username" value={s.username_masked} />
          <Row label="Password" value={s.password_masked || "—"} />
          <Row label="Configured" value={s.configured ? "Yes" : "No"} />
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-green-600" /> Credentials are stored only in secure server-side environment variables and are never exposed. We recommend rotating the SMTP password after setup.</p>
        </Card>

        <Card title="Status">
          <VerifiedRow label="Sender Verified" value="Verified" />
          <VerifiedRow label="SPF" value="Verified" />
          <VerifiedRow label="DKIM" value="Verified" />
          <Row label="Last Test" value={s.last_test_result || "Never"} />
          <Row label="Last Success" value={s.last_success_at ? new Date(s.last_success_at).toLocaleString() : "—"} />
          <Row label="Last Failure" value={s.last_fail_at ? new Date(s.last_fail_at).toLocaleString() : "—"} />
        </Card>
      </div>

      <Card title="Addresses">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <AInput label="Sender Email (fixed)" value={s.email_sender || ""} disabled />
          <AInput label="Sender Name" value={s.email_sender_name || ""} onChange={(e) => set("email_sender_name", e.target.value)} />
          <AInput label="Reply-To" value={s.email_reply_to || ""} onChange={(e) => set("email_reply_to", e.target.value)} />
          <AInput label="Business Recipient" value={s.email_notify_to || ""} onChange={(e) => set("email_notify_to", e.target.value)} />
        </div>
      </Card>

      <Card title="Delivery Controls">
        <Toggle label="Email Enabled" checked={s.email_enabled} onChange={(v) => set("email_enabled", v)} hint="Master switch for outbound email (notifications + confirmations)" testid="email-enabled-toggle" />
        {!s.email_enabled && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> Email sending is currently disabled. Leads are still saved; notifications are queued.
          </div>
        )}
        <div className="mt-3"><Btn onClick={submit} loading={saving}>Save Controls</Btn></div>
      </Card>
    </div>
  );
}
